// Phone-number redaction for the central call log (CDCSF-5447).
// Pure module (no Node APIs) so the browser client can bundle it too.

const MASK_CHAR = "X";
const MASKED_DIGITS = 5;

/**
 * Reduce a raw phone identifier to "+" and digits only.
 * Handles the shapes seen in ACS / Graph: "+44 20 7946 0000", "tel:+44...",
 * "4:+44..." (ACS rawId), "sip:+44...@host;user=phone". Already-redacted
 * input keeps its mask characters so redaction is idempotent.
 *
 * @param {string} raw
 * @returns {string} "+"-prefixed or bare digit/mask string, or "" if nothing usable
 */
function normalise(raw) {
  let value = String(raw).trim();
  value = value.replace(/^(tel|sip|sips):/i, "");
  value = value.replace(/^\d+:/, ""); // ACS rawId kind prefix, e.g. "4:" for phone

  value = value.split(/[@;]/)[0];
  // Drop an extension ("x123", "ext. 123", "#123") so it can't shift which digits get masked.
  value = value.replace(/\s*(?:ext\.?|extension|x|#)\s*\d+\s*$/i, "");

  const plusAt = value.lastIndexOf("+");
  const hasPlus = plusAt !== -1;
  if (hasPlus) value = value.slice(plusAt + 1);

  const body = value.replace(new RegExp(`[^0-9${MASK_CHAR}]`, "g"), "");
  if (!body) return "";
  return hasPlus ? `+${body}` : body;
}

/**
 * Mask the last 5 digits of a phone number. Numbers with 5 or fewer digits are
 * fully masked. Returns null when the input holds no digits at all (e.g.
 * "anonymous"), so a full number can never pass through unchanged.
 *
 * @param {string|null|undefined} raw
 * @returns {string|null} e.g. "+4420794XXXXX"
 */
function redactNumber(raw) {
  if (raw === null || raw === undefined) return null;

  const normalised = normalise(raw);
  const prefix = normalised.startsWith("+") ? "+" : "";
  const body = prefix ? normalised.slice(1) : normalised;
  if (!/[0-9]/.test(body) && !body.includes(MASK_CHAR)) return null;

  const keep = Math.max(0, body.length - MASKED_DIGITS);
  return prefix + body.slice(0, keep) + MASK_CHAR.repeat(body.length - keep);
}

module.exports = { redactNumber, normalise, MASK_CHAR, MASKED_DIGITS };
